import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useAuth } from '@trafiflow/shared-ui'
import apiClient from '../../../shared/lib/api'
import type { NextStore } from '../../stores/types'
import { labelClass, selectClass } from '../styles/class-utils'
import {
  resolveTenantId,
  setSelectedTenantId,
  subscribeTenantChanges,
} from '../lib/tenant-selection'

interface TenantSelectorProps {
  compact?: boolean
}

interface StoresResponse {
  stores?: NextStore[]
}

export function TenantSelector({ compact = false }: TenantSelectorProps) {
  const { user } = useAuth()
  const [stores, setStores] = useState<NextStore[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string>(() => resolveTenantId(user?.tenantId))

  const loadStores = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiClient.get<StoresResponse>('/next-stores')
      const list = Array.isArray(response.data?.stores) ? response.data.stores : []
      setStores(list)
      if (list.length === 0) return

      const preferredId = resolveTenantId(user?.tenantId)
      const validId = list.some((store) => store.tenantId === preferredId)
        ? preferredId
        : list[0].tenantId
      if (validId) {
        setSelectedTenantId(validId)
        setSelectedId(validId)
      }
    } catch (error) {
      console.error('[TenantSelector] Failed to load stores:', error)
      setStores([])
    } finally {
      setLoading(false)
    }
  }, [user?.tenantId])

  useEffect(() => {
    loadStores()
  }, [loadStores])

  useEffect(() => subscribeTenantChanges(() => {
    setSelectedId(resolveTenantId(user?.tenantId))
  }), [user?.tenantId])

  const options = useMemo(
    () => stores.map((store) => ({ id: store.tenantId, label: store.name || store.tenantId })),
    [stores],
  )

  if (stores.length === 0 && !loading) return null

  return (
    <div className={compact ? 'min-w-[220px]' : ''}>
      {!compact && <label className={labelClass}>Store</label>}
      <div className="relative">
        <select
          className={`${selectClass} ${compact ? 'h-8 py-1.5 text-xs pr-8' : ''}`}
          value={selectedId}
          onChange={(e) => {
            const nextId = e.target.value
            setSelectedTenantId(nextId)
            setSelectedId(nextId)
          }}
          disabled={loading}
          aria-label="Store selector"
        >
          {loading && <option value="">Loading stores...</option>}
          {!loading && options.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        <ChevronDown className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-fn-text-muted ${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
      </div>
    </div>
  )
}
